// The U1 screens. Each page is its own chunk (route splitting), so the
// landing page loads only what it needs (NFR3.2).
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useCaller } from './caller-context';

const SignInPage = lazy(async () => ({
  default: (await import('../sign-in/sign-in-page')).SignInPage,
}));
const SignInReturnPage = lazy(async () => ({
  default: (await import('../sign-in/sign-in-return-page')).SignInReturnPage,
}));
const SignUpPage = lazy(async () => ({
  default: (await import('../sign-up/sign-up-page')).SignUpPage,
}));
const PrivacyPage = lazy(async () => ({
  default: (await import('../privacy/privacy-page')).PrivacyPage,
}));
const LandingPage = lazy(async () => ({
  default: (await import('../landing/landing-page')).LandingPage,
}));
const JoinPage = lazy(async () => ({ default: (await import('../join/join-page')).JoinPage }));
const DashboardPage = lazy(async () => ({
  default: (await import('../dashboard/dashboard-page')).DashboardPage,
}));
const WebinarFormPage = lazy(async () => ({
  default: (await import('../offering-form/webinar-form-page')).WebinarFormPage,
}));
const HomePage = lazy(async () => ({ default: (await import('../home/home-page')).HomePage }));
// U2's pages, each its own chunk; the Markdown renderer loads only with the
// pages that show Markdown (NFR3.4).
const AccountPage = lazy(async () => ({
  default: (await import('../account/account-page')).AccountPage,
}));
const PrivacyChoicesPage = lazy(async () => ({
  default: (await import('../privacy-choices/privacy-choices-page')).PrivacyChoicesPage,
}));
const UnsubscribePage = lazy(async () => ({
  default: (await import('../unsubscribe/unsubscribe-page')).UnsubscribePage,
}));
const AdminPrivacyNoticePage = lazy(async () => ({
  default: (await import('../admin-notice/admin-privacy-notice-page')).AdminPrivacyNoticePage,
}));

// U3's pages, each its own chunk. The application chunk holds the photo
// shrinking and the bio preview, so the landing page never loads them.
const HostEntryPage = lazy(async () => ({
  default: (await import('../host/host-entry-page')).HostEntryPage,
}));
const CoachApplicationPage = lazy(async () => ({
  default: (await import('../coach-application/coach-application-page')).CoachApplicationPage,
}));
const CoachProfilePage = lazy(async () => ({
  default: (await import('../coach-application/coach-profile-page')).CoachProfilePage,
}));
const PublicCoachPage = lazy(async () => ({
  default: (await import('../coach-public/public-coach-page')).PublicCoachPage,
}));
const CoachTermsPage = lazy(async () => ({
  default: (await import('../coach-public/coach-terms-page')).CoachTermsPage,
}));
const AdminCoachesPage = lazy(async () => ({
  default: (await import('../admin-coaches/admin-coaches-page')).AdminCoachesPage,
}));
const AdminCoachTermsPage = lazy(async () => ({
  default: (await import('../admin-coach-terms/admin-coach-terms-page')).AdminCoachTermsPage,
}));

// U4's pages: meeting settings and the Zoom return page, one route chunk
// (NFR3.7), so the landing page never loads them.
const loadMeetingSettings = () => import('../meeting-settings');
const MeetingSettingsPage = lazy(async () => ({
  default: (await loadMeetingSettings()).MeetingSettingsPage,
}));
const ZoomReturnPage = lazy(async () => ({
  default: (await loadMeetingSettings()).ZoomReturnPage,
}));

// U6's pages, each its own chunk (NFR3.9), so the landing page never loads them.
const MyRegistrationsPage = lazy(async () => ({
  default: (await import('../my-registrations')).MyRegistrationsPage,
}));
const RegistrantsPage = lazy(async () => ({
  default: (await import('../registrants')).RegistrantsPage,
}));

// A page that needs sign-in sends the person to sign-in with a way back
// (BR1.10); a pending sign-up goes to sign-up.
function Signed(props: { children: ReactNode; isCoachOnly?: boolean }) {
  const { me, isLoading, isUnreachable } = useCaller();
  const location = useLocation();
  if (isLoading) return <p role="status">Loading…</p>;
  // The shell's banner says the connection is down, with Try again. The
  // person may be signed in, so they are not sent to sign-in (Step 26).
  if (isUnreachable) return null;
  if (!me)
    return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  if (me.isSignUpPending) return <Navigate to="/sign-up" replace />;
  if (props.isCoachOnly && me.coachStatus !== 'approved') return <Navigate to="/" replace />;
  return <>{props.children}</>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<p role="status">Loading…</p>}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-in/return" element={<SignInReturnPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/webinars/:slug" element={<LandingPage />} />
        <Route
          path="/join/:sessionId"
          element={
            <Signed>
              <JoinPage />
            </Signed>
          }
        />
        <Route
          path="/coach"
          element={
            <Signed isCoachOnly>
              <DashboardPage />
            </Signed>
          }
        />
        <Route
          path="/coach/webinars/new"
          element={
            <Signed isCoachOnly>
              <WebinarFormPage />
            </Signed>
          }
        />
        {/* U5 (W1): the edit page. A suspended coach is sent to the dashboard. */}
        <Route
          path="/coach/webinars/:offeringId/edit"
          element={
            <Signed>
              <WebinarFormPage />
            </Signed>
          }
        />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route
          path="/account"
          element={
            <Signed>
              <AccountPage />
            </Signed>
          }
        />
        <Route
          path="/account/privacy"
          element={
            <Signed>
              <PrivacyChoicesPage />
            </Signed>
          }
        />
        <Route
          path="/admin/privacy-notice"
          element={
            <Signed>
              <AdminPrivacyNoticePage />
            </Signed>
          }
        />
        {/* U3 */}
        <Route path="/host" element={<HostEntryPage />} />
        <Route
          path="/coach/application"
          element={
            <Signed>
              <CoachApplicationPage />
            </Signed>
          }
        />
        <Route
          path="/coach/profile"
          element={
            <Signed>
              <CoachProfilePage />
            </Signed>
          }
        />
        <Route path="/coaches/:coachProfileId" element={<PublicCoachPage />} />
        <Route path="/coach-terms" element={<CoachTermsPage />} />
        <Route
          path="/admin/coaches"
          element={
            <Signed>
              <AdminCoachesPage />
            </Signed>
          }
        />
        <Route
          path="/admin/coach-terms"
          element={
            <Signed>
              <AdminCoachTermsPage />
            </Signed>
          }
        />
        {/* U4: any coach sees the connection; only an approved coach connects. */}
        <Route
          path="/coach/meeting-settings"
          element={
            <Signed>
              <MeetingSettingsPage />
            </Signed>
          }
        />
        <Route
          path="/coach/zoom/return"
          element={
            <Signed isCoachOnly>
              <ZoomReturnPage />
            </Signed>
          }
        />
        {/* U6: the API answers 404 while registrationsSelfService is off. */}
        <Route
          path="/registrations"
          element={
            <Signed>
              <MyRegistrationsPage />
            </Signed>
          }
        />
        <Route
          path="/coach/webinars/:offeringId/registrants"
          element={
            <Signed>
              <RegistrantsPage />
            </Signed>
          }
        />
        <Route path="*" element={<h1>Page not found</h1>} />
      </Routes>
    </Suspense>
  );
}
